import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Heart, Check, X, Eye, EyeOff, Search, Users, Loader2, Edit, Trash2, ChevronLeft, ChevronRight, Download, User, FileText, Upload, ToggleLeft, ToggleRight, KeyRound, History, Clock, ArrowRight, MessageSquare, AlertCircle, Filter, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import { resolveFileUrl } from '../../utils/fileUrl';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import PaginationBar from '../../components/PaginationBar';
import { formatDisplayDate, formatDisplayDateTime } from '../../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LIMIT = 20;

const PS_AGE_RANGES = [
  { label: 'All Ages', min: '', max: '' },
  { label: '18-25', min: 18, max: 25 },
  { label: '25-30', min: 25, max: 30 },
  { label: '30-35', min: 30, max: 35 },
  { label: '35-45', min: 35, max: 45 },
  { label: '45+', min: 45, max: 100 },
];

const PSManagement = ({ attendingFilter }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  const [editData, setEditData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [ebook, setEbook] = useState(null);
  const [uploadingEbook, setUploadingEbook] = useState(false);
  const [uploadingAttendeePhoto, setUploadingAttendeePhoto] = useState(null);
  const attendeePhotoRefs = useRef({});
  const ebookRef = useRef(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminComments, setAdminComments] = useState('');
  const [savingComments, setSavingComments] = useState(false);
  const [photoModal, setPhotoModal] = useState(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [cityFilter, setCityFilter] = useState('');
  const [nativePlaceFilter, setNativePlaceFilter] = useState('');
  const [nukhFilter, setNukhFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [ageRangeFilter, setAgeRangeFilter] = useState('');
  const [educationFilter, setEducationFilter] = useState('');
  const [occupationFilter, setOccupationFilter] = useState('');
  const [maritalStatusFilter, setMaritalStatusFilter] = useState('');
  const [engagementFilter, setEngagementFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ nukhs: [], states: [] });

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => { fetchData(); fetchEbook(); }, [statusFilter, page, searchTerm, cityFilter, nativePlaceFilter, nukhFilter, genderFilter, ageRangeFilter, educationFilter, occupationFilter, maritalStatusFilter, engagementFilter, attendingFilter]);

  useEffect(() => {
    axios.get(`${API_URL}/api/ps/options`).then(res => {
      setFilterOptions({ nukhs: res.data.nukhs || [], states: res.data.states || [] });
    }).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (attendingFilter !== undefined) params.attending = attendingFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (cityFilter) params.city = cityFilter;
      if (nativePlaceFilter) params.native_place = nativePlaceFilter;
      if (nukhFilter) params.nukh = nukhFilter;
      if (genderFilter) params.gender = genderFilter;
      if (educationFilter) params.education = educationFilter;
      if (occupationFilter) params.occupation = occupationFilter;
      if (maritalStatusFilter) params.marital_status = maritalStatusFilter;
      if (engagementFilter) params.engaged = engagementFilter;
      if (ageRangeFilter) {
        const range = PS_AGE_RANGES.find(r => r.label === ageRangeFilter);
        if (range && range.min !== '') {
          params.age_min = range.min;
          params.age_max = range.max;
        }
      }
      const statsParams = {};
      if (attendingFilter !== undefined) statsParams.attending = attendingFilter;
      if (engagementFilter) statsParams.engaged = engagementFilter;
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ps/admin/users`, { headers, params }),
        axios.get(`${API_URL}/api/ps/admin/stats`, { headers, params: statsParams })
      ]);
      setUsers(usersRes.data.users);
      setTotal(usersRes.data.total);
      setStats(statsRes.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const fetchEbook = async () => {
    try { const res = await axios.get(`${API_URL}/api/ps/admin/ebook`, { headers }); setEbook(res.data); } catch {}
  };

  const handleEbookUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingEbook(true);
    try {
      const fd = new FormData(); fd.append('ebook', file); fd.append('title', 'Parichay Sangam 2026 eBook'); fd.append('isPublished', 'false');
      const res = await axios.post(`${API_URL}/api/ps/admin/ebook`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message); fetchEbook();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingEbook(false); }
  };

  const toggleEbookPublish = async () => {
    try {
      const res = await axios.put(`${API_URL}/api/ps/admin/ebook/publish`, { isPublished: !ebook?.isPublished }, { headers });
      toast.success(res.data.message); fetchEbook();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSearch = () => { setPage(0); setSearchTerm(searchInput); };

  const clearAllFilters = () => {
    setSearchInput(''); setSearchTerm(''); setStatusFilter('');
    setCityFilter(''); setNativePlaceFilter(''); setNukhFilter('');
    setGenderFilter(''); setAgeRangeFilter(''); setEducationFilter('');
    setOccupationFilter(''); setMaritalStatusFilter(''); setEngagementFilter(''); setPage(0);
  };

  const activeFilterCount = [statusFilter, cityFilter, nativePlaceFilter, nukhFilter, genderFilter, ageRangeFilter, educationFilter, occupationFilter, maritalStatusFilter, engagementFilter].filter(Boolean).length;

  const updateStatus = async (userId, newStatus, reason = '', comments = '') => {
    try {
      await axios.put(`${API_URL}/api/ps/admin/users/${userId}/status`, {
        status: newStatus,
        rejectionReason: reason,
        adminComments: comments,
      }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      if (selectedUser?.id === userId) {
        setSelectedUser(prev => ({
          ...prev, status: newStatus,
          rejectionReason: newStatus === 'rejected' ? reason : '',
          adminComments: comments || prev?.adminComments || '',
        }));
      }
    } catch { toast.error('Failed to update status'); }
  };

  const handleReject = (userId) => {
    setRejectModal(userId);
    setRejectionReason('');
  };

  const confirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    await updateStatus(rejectModal, 'rejected', rejectionReason.trim());
    setRejectModal(null);
    setRejectionReason('');
  };

  const saveAdminComments = async (userId) => {
    setSavingComments(true);
    try {
      await axios.put(`${API_URL}/api/ps/admin/users/${userId}/comments`, { adminComments }, { headers });
      toast.success('Admin comments saved');
      if (selectedUser?.id === userId) {
        setSelectedUser(prev => ({ ...prev, adminComments }));
      }
    } catch { toast.error('Failed to save comments'); }
    finally { setSavingComments(false); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/ps/admin/users/${userId}`, { headers });
      toast.success('Deleted'); setSelectedUser(null); setViewMode(null); fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const downloadIdCards = async (userId, familyCode) => {
    try {
      toast.info('Generating ID cards...');
      const res = await axios.get(`${API_URL}/api/ps/admin/users/${userId}/idcards`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ID_Cards_${familyCode || userId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('ID cards downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate ID cards');
    }
  };


  const openView = async (user) => {
    try {
      const res = await axios.get(`${API_URL}/api/ps/admin/users/${user.id}`, { headers });
      setSelectedUser(res.data); setEditData(JSON.parse(JSON.stringify(res.data))); setViewMode('view');
      setShowHistory(false); setEditHistory([]);
      setAdminComments(res.data.adminComments || '');
    } catch { toast.error('Failed to load'); }
  };

  const fetchHistory = async (userId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ps/admin/users/${userId}/history`, { headers });
      setEditHistory(res.data.history || []);
      setShowHistory(true);
    } catch { toast.error('Failed to load edit history'); }
    finally { setHistoryLoading(false); }
  };

  const openEdit = () => { setEditData(JSON.parse(JSON.stringify(selectedUser))); setViewMode('edit'); };

  const handleCandidateFieldChange = (cidx, field, value) => {
    setEditData(prev => {
      const candidates = [...(prev.candidates || [])];
      candidates[cidx] = { ...candidates[cidx], [field]: value };
      return { ...prev, candidates };
    });
  };

  const handleFamilyFieldChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResettingPassword(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/reset-password`, {
        email: resetPasswordModal.email,
        userType: 'ps_user',
        newPassword
      }, { headers });
      toast.success(`Password reset for ${resetPasswordModal.name}`);
      setResetPasswordModal(null);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
    finally { setResettingPassword(false); }
  };

  const handleAttendeeFieldChange = (aidx, field, value) => {
    setEditData(prev => {
      const attendees = [...(prev.attendees || [])];
      attendees[aidx] = { ...attendees[aidx], [field]: value };
      return { ...prev, attendees };
    });
  };

  const handleAdminAttendeePhotoUpload = async (userId, aidx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAttendeePhoto(aidx);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('attendeeIndex', aidx.toString());
      const res = await axios.post(`${API_URL}/api/ps/admin/users/${userId}/attendee-photo`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      const newUrl = res.data.photoUrl;
      setSelectedUser(prev => {
        const attendees = [...(prev.attendees || [])];
        attendees[aidx] = { ...attendees[aidx], photoUrl: newUrl };
        return { ...prev, attendees };
      });
      setEditData(prev => {
        const attendees = [...(prev.attendees || [])];
        attendees[aidx] = { ...attendees[aidx], photoUrl: newUrl };
        return { ...prev, attendees };
      });
      toast.success('Attendee photo uploaded');
    } catch { toast.error('Photo upload failed'); }
    finally { setUploadingAttendeePhoto(null); }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await axios.put(`${API_URL}/api/ps/admin/users/${selectedUser.id}`, editData, { headers });
      setSelectedUser(res.data.user); setViewMode('view'); toast.success('Profile updated'); fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSavingEdit(false); }
  };

  const StatusBadge = ({ status, small }) => {
    const s = { approved: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700' };
    return (<span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${s[status] || s.pending} ${small ? 'text-xs' : 'text-sm'}`}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</span>);
  };

  const [exporting, setExporting] = useState(false);
  const bulkUploadRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL records from backend (no pagination limit)
      const exportParams = { limit: 10000, skip: 0 };
      if (attendingFilter !== undefined) exportParams.attending = attendingFilter;
      if (engagementFilter) exportParams.engaged = engagementFilter;
      const res = await axios.get(`${API_URL}/api/ps/admin/users`, { headers, params: exportParams });
      const allUsers = res.data.users || [];
      if (!allUsers.length) { toast.error('No data to export'); setExporting(false); return; }

      const familyFields = ['familyCode','familyEmail','guardianName','guardianWhatsapp','status','attendingInPerson','paymentMode','paymentDetails','paymentAmount','paymentProofUrl','registrationDate','lastUpdated','rejectionReason','adminComments','additionalInfo'];
      const candidateFields = ['candidateCode','name','fatherName','motherName','nukh','gender','dateOfBirth','birthTime','birthPlace','maritalStatus','height','weight','complexion','bloodGroup','mapiOrManglik','unmarriedBrothers','marriedBrothers','unmarriedSisters','marriedSisters','maternalName','nukhOfMaternals','placeOfMaternals','education','occupation','workingAt','annualIncome','phone','whatsapp','candidateEmail','currentAddress','currentPlace','currentState','currentCountry','nativePlace','nativeState','hobbies','aboutCandidate','partnerPreference','photoUrl'];
      const maxAttendees = Math.max(1, ...allUsers.map(u => (u.attendees || []).length));
      const attHeaders = [];
      for (let i = 1; i <= maxAttendees; i++) { attHeaders.push(`attendee${i}Name`,`attendee${i}Relation`,`attendee${i}Phone`,`attendee${i}PhotoUrl`); }

      const rows = [];
      allUsers.forEach(u => {
        const fam = {
          familyCode: u.familyCode, familyEmail: u.email, guardianName: u.guardianName, guardianWhatsapp: u.guardianWhatsapp,
          status: u.status, attendingInPerson: u.attendingInPerson ? 'Yes' : 'No', paymentMode: u.paymentMode,
          paymentDetails: u.paymentDetails, paymentAmount: u.paymentAmount, paymentProofUrl: u.paymentProofUrl,
          registrationDate: u.registrationDate, lastUpdated: u.lastUpdated, rejectionReason: u.rejectionReason, adminComments: u.adminComments,
          additionalInfo: u.additionalInfo,
        };
        const attCols = {};
        (u.attendees || []).forEach((a, i) => { attCols[`attendee${i+1}Name`] = a.name; attCols[`attendee${i+1}Relation`] = a.relation; attCols[`attendee${i+1}Phone`] = a.phone; attCols[`attendee${i+1}PhotoUrl`] = a.photoUrl; });
        (u.candidates || []).forEach(c => {
          const row = {};
          familyFields.forEach(f => { row[f] = fam[f] ?? ''; });
          candidateFields.forEach(f => { row[f] = f === 'candidateEmail' ? (c.email || '') : (c[f] || ''); });
          attHeaders.forEach(f => { row[f] = attCols[f] || ''; });
          rows.push(row);
        });
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'PS 2026 Registrations');
      XLSX.writeFile(wb, `parichay_sangam_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${rows.length} records (${allUsers.length} families)`);
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleBulkUpload = async (file) => {
    if (!file) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/ps/admin/bulk-update-fields`, fd, {
        headers: { ...{ Authorization: `Bearer ${getToken()}` }, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      if (res.data.errors?.length) {
        toast.error(`${res.data.errors.length} errors - check console`);
        console.log('Bulk update errors:', res.data.errors);
      }
      fetchData();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkUploading(false);
      if (bulkUploadRef.current) bulkUploadRef.current.value = '';
    }
  };

  const [regenProgress, setRegenProgress] = useState({ current: 0, total: 0 });
  const handleBulkRegenerate = async () => {
    if (!window.confirm('Regenerate and email updated ID cards (+ timetable) to ALL approved attending families?')) return;
    setRegenerating(true);
    setRegenProgress({ current: 0, total: 0 });
    try {
      const countRes = await axios.get(`${API_URL}/api/ps/admin/bulk-regenerate-idcards/count`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const total = countRes.data.total || 0;
      const batchSize = countRes.data.batchSize || 10;
      if (total === 0) { toast.error('No approved attending families'); return; }
      const totalBatches = Math.ceil(total / batchSize);
      toast.success(`Sending emails to ${total} families in ${totalBatches} batch${totalBatches > 1 ? 'es' : ''}…`);
      let sentTotal = 0;
      let failedTotal = 0;
      for (let i = 0; i < totalBatches; i++) {
        const skip = i * batchSize;
        setRegenProgress({ current: i + 1, total: totalBatches });
        const res = await axios.post(
          `${API_URL}/api/ps/admin/bulk-regenerate-idcards?skip=${skip}&limit=${batchSize}`,
          {},
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        sentTotal += res.data.sent || 0;
        failedTotal += res.data.failed || 0;
      }
      toast.success(`Done! Emailed ${sentTotal} famil${sentTotal === 1 ? 'y' : 'ies'}${failedTotal > 0 ? `, ${failedTotal} failed` : ''}.`);
    } catch (err) {
      toast.error('Regeneration failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegenerating(false);
      setRegenProgress({ current: 0, total: 0 });
    }
  };

  const [downloadingA3, setDownloadingA3] = useState(false);
  const [a3Progress, setA3Progress] = useState({ current: 0, total: 0 });
  const handleDownloadA3 = async () => {
    setDownloadingA3(true);
    setA3Progress({ current: 0, total: 0 });
    try {
      const countRes = await axios.get(`${API_URL}/api/ps/admin/idcards/a3-sheet/count`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const total = countRes.data.total || 0;
      const batchSize = countRes.data.batchSize || 60;
      if (total === 0) {
        toast.error('No approved attendees with ID cards');
        return;
      }
      const totalBatches = Math.ceil(total / batchSize);
      toast.success(`Generating ${totalBatches} A3 PDF${totalBatches > 1 ? 's' : ''} for ${total} cards…`);
      let downloaded = 0;
      for (let i = 0; i < totalBatches; i++) {
        const skip = i * batchSize;
        setA3Progress({ current: i + 1, total: totalBatches });
        const res = await axios.get(
          `${API_URL}/api/ps/admin/idcards/a3-sheet?skip=${skip}&limit=${batchSize}`,
          { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `PS2026_Attendees_A3Sheets_part${i + 1}of${totalBatches}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        downloaded += 1;
        // small delay so browser handles each download separately
        await new Promise((r) => setTimeout(r, 400));
      }
      toast.success(`Downloaded ${downloaded} A3 PDF file${downloaded > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error('A3 sheet failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloadingA3(false);
      setA3Progress({ current: 0, total: 0 });
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  // ============ DETAIL / EDIT VIEW ============
  if (selectedUser && viewMode) {
    const d = viewMode === 'edit' ? editData : selectedUser;
    const isEditing = viewMode === 'edit';

    const renderCandidateField = (cidx, label, field, type = 'text', options = null) => {
      const candidate = d.candidates?.[cidx] || {};
      const val = candidate[field] || '';
      const displayVal = field === 'dateOfBirth' ? (formatDisplayDate(val) || '\u2014') : (val || '\u2014');
      if (!isEditing) return (<div><p className="text-xs text-gray-400">{label}</p><p className="text-sm font-medium text-gray-800">{displayVal}</p></div>);
      if (options) return (<div><Label className="text-xs">{label}</Label><select value={val} onChange={(e) => handleCandidateFieldChange(cidx, field, e.target.value)} className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm"><option value="">Select</option>{options.map(o => <option key={o} value={o}>{o}</option>)}</select></div>);
      return (<div><Label className="text-xs">{label}</Label><Input type={type} value={val} onChange={(e) => handleCandidateFieldChange(cidx, field, e.target.value)} className="mt-1 h-9 text-sm" /></div>);
    };

    const renderCandidateTextarea = (cidx, label, field) => {
      const val = (d.candidates?.[cidx] || {})[field] || '';
      if (!isEditing) return (<div><p className="text-xs text-gray-400">{label}</p><p className="text-sm text-gray-800 whitespace-pre-wrap">{val || '\u2014'}</p></div>);
      return (<div><Label className="text-xs">{label}</Label><Textarea value={val} onChange={(e) => handleCandidateFieldChange(cidx, field, e.target.value)} rows={2} className="mt-1 text-sm" /></div>);
    };

    const renderFamilyField = (label, field) => {
      const val = d[field] || '';
      if (!isEditing) return (<div><p className="text-xs text-gray-400">{label}</p><p className="text-sm font-medium text-gray-800">{val || '\u2014'}</p></div>);
      return (<div><Label className="text-xs">{label}</Label><Input value={val} onChange={(e) => handleFamilyFieldChange(field, e.target.value)} className="mt-1 h-9 text-sm" /></div>);
    };

    return (
      <>
      <div className="p-6" data-testid="admin-ps-detail">
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" size="sm" onClick={() => { setSelectedUser(null); setViewMode(null); }}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to List
          </Button>
          <div className="flex items-center gap-2">
            <StatusBadge status={d.status} />
            {!isEditing && (
              <>
                <Button size="sm" variant="outline" onClick={openEdit} data-testid="admin-ps-edit-btn"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" className="text-purple-600" onClick={() => fetchHistory(d.id)} data-testid="admin-ps-history-btn">
                  {historyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <History className="w-4 h-4 mr-1" />} History
                </Button>
                <Button size="sm" variant="outline" className="text-blue-600" onClick={() => downloadIdCards(d.id, d.familyCode)} data-testid="admin-ps-idcard-btn"><Download className="w-4 h-4 mr-1" /> ID Cards</Button>
                <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateStatus(d.id, 'approved')} data-testid="admin-ps-approve-btn"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(d.id)} data-testid="admin-ps-reject-btn"><X className="w-4 h-4 mr-1" /> Reject</Button>
                <Button size="sm" variant="destructive" onClick={() => deleteUser(d.id)} data-testid="admin-ps-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </>
            )}
            {isEditing && (
              <>
                <Button size="sm" variant="outline" onClick={() => setViewMode('view')}>Cancel</Button>
                <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={saveEdit} disabled={savingEdit} data-testid="admin-ps-save-btn">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Edit History Panel */}
        {showHistory && (
          <Card className="p-4 mb-4 border-purple-200 bg-purple-50/50" data-testid="admin-ps-history-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-purple-600" />
                <h4 className="font-semibold text-sm text-purple-900">Edit History / Audit Trail</h4>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{editHistory.length} entries</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setShowHistory(false)} className="text-purple-600 h-7 px-2">
                <X className="w-4 h-4" />
              </Button>
            </div>
            {editHistory.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No edit history recorded yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {editHistory.map((entry, idx) => (
                  <div key={entry.id || idx} className="bg-white rounded-lg border border-gray-100 p-3" data-testid={`history-entry-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          entry.changeType === 'status_change' ? 'bg-blue-100 text-blue-700' :
                          entry.editedByRole === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {entry.changeType === 'status_change' ? 'Status Change' : entry.editedByRole === 'admin' ? 'Admin Edit' : 'User Edit'}
                        </span>
                        <span className="text-xs text-gray-500">by <span className="font-medium">{entry.editedBy}</span></span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        {formatDisplayDateTime(entry.timestamp)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {entry.changes?.map((change, cidx) => (
                        <div key={cidx} className="flex items-center gap-2 text-xs py-1 border-t border-gray-50 first:border-t-0">
                          <span className="font-medium text-gray-700 min-w-[120px]">{change.label}:</span>
                          <span className="text-red-500 line-through truncate max-w-[150px]" title={change.oldValue}>{change.oldValue}</span>
                          <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-green-600 font-medium truncate max-w-[150px]" title={change.newValue}>{change.newValue}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{entry.totalChanges} field(s) changed</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Family Header */}
        <Card className="p-6 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><p className="text-xs text-gray-400">Family Code</p><p className="text-lg font-bold text-orange-700">{d.familyCode}</p></div>
            {renderFamilyField("Email", "email")}
            {renderFamilyField("Guardian Name", "guardianName")}
            {renderFamilyField("Guardian WhatsApp", "guardianWhatsapp")}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
            <span>Candidates: {d.candidates?.length || 0}</span>
            <span>Attendees: {d.attendees?.length || 0}</span>
            <span>Attending Event in-person: <strong className={d.attendingInPerson ? 'text-green-600' : 'text-red-600'}>{d.attendingInPerson ? 'Yes' : 'No'}</strong></span>
            <span>Fee: {'\u20B9'}{d.paymentAmount?.toLocaleString()}</span>
            <span>Registered: {d.registrationDate ? formatDisplayDate(d.registrationDate) : '\u2014'}</span>
          </div>
        </Card>

        {/* Rejection Reason Display */}
        {d.rejectionReason && d.status === 'rejected' && (
          <Card className="p-4 mb-4 border-red-200 bg-red-50" data-testid="ps-rejection-reason-card">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-red-800">Rejection Reason</h4>
                <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{d.rejectionReason}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Additional Info / Feedback from Applicant */}
        {(d.additionalInfo || d.infoAttachments?.length > 0) && (
          <Card className="p-4 mb-4 border-gray-200 bg-gray-50/50" data-testid="ps-additional-info-card">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">Additional Info / Feedback (from applicant)</h4>
            {d.additionalInfo && <p className="text-sm text-gray-600 whitespace-pre-wrap">{d.additionalInfo}</p>}
            {d.infoAttachments?.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-gray-500">Attachments:</p>
                {d.infoAttachments.map((att, idx) => (
                  <a key={idx} href={resolveFileUrl(att.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline" data-testid={`admin-info-attachment-${idx}`}>
                    <FileText className="w-3.5 h-3.5" /> {att.name}
                  </a>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Admin Comments Section */}
        <Card className="p-4 mb-4 border-amber-200 bg-amber-50/50" data-testid="ps-admin-comments-card">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">Admin Comments / Feedback</h4>
              <Textarea
                value={adminComments}
                onChange={(e) => setAdminComments(e.target.value)}
                placeholder="Add comments for the applicant (e.g., corrections needed, missing info)..."
                rows={3}
                className="text-sm bg-white"
                data-testid="ps-admin-comments-input"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-amber-600">These comments will be visible to the applicant on their dashboard.</p>
                <Button
                  size="sm"
                  onClick={() => saveAdminComments(d.id)}
                  disabled={savingComments || adminComments === (d.adminComments || '')}
                  data-testid="ps-admin-comments-save"
                >
                  {savingComments ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Save Comments
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Candidates */}
        {d.candidates?.map((candidate, cidx) => (
          <Card key={cidx} className="p-4 mb-4">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-orange-400 transition-all" onClick={() => candidate.photoUrl && setPhotoModal({ url: resolveFileUrl(candidate.photoUrl), name: candidate.name || 'Candidate', rawUrl: candidate.photoUrl })} data-testid={`candidate-photo-thumb-${cidx}`}>
                {candidate.photoUrl ? <img src={resolveFileUrl(candidate.photoUrl)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-8 h-8 text-gray-300" /></div>}
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 flex items-center gap-2">
                  {candidate.name || 'N/A'}
                  {candidate.hiddenFromDirectory && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 border border-gray-300 inline-flex items-center gap-1" data-testid={`hidden-badge-${cidx}`}>
                      <EyeOff className="w-3 h-3" /> Hidden
                    </span>
                  )}
                </p>
                <p className="text-xs text-orange-600 font-medium">{candidate.candidateCode} | {candidate.gender} | {formatDisplayDate(candidate.dateOfBirth)}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
              {/* Engagement toggle — admin-only flag that hides "Express Interest" on the directory */}
              <button
                type="button"
                onClick={async () => {
                  const nextVal = !candidate.isEngaged;
                  const confirmMsg = nextVal
                    ? `Mark ${candidate.name || candidate.candidateCode} as ENGAGED?\nThe directory will hide the Express Interest button and show "Parichay Ho Gaya!" instead.`
                    : `Remove ENGAGED status for ${candidate.name || candidate.candidateCode}?`;
                  if (!window.confirm(confirmMsg)) return;
                  try {
                    await axios.put(
                      `${API_URL}/api/ps/admin/candidates/${candidate.candidateCode}/engaged`,
                      { engaged: nextVal },
                      { headers }
                    );
                    toast.success(nextVal ? 'Marked as ENGAGED' : 'Engagement removed');
                    setSelectedUser((prev) => {
                      if (!prev) return prev;
                      const cands = [...(prev.candidates || [])];
                      cands[cidx] = { ...cands[cidx], isEngaged: nextVal, engagedAt: nextVal ? new Date().toISOString() : null };
                      return { ...prev, candidates: cands };
                    });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to update engagement');
                  }
                }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  candidate.isEngaged
                    ? 'bg-pink-50 border-pink-300 text-pink-700 hover:bg-pink-100'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-pink-300 hover:text-pink-700'
                }`}
                data-testid={`engagement-toggle-${cidx}`}
              >
                {candidate.isEngaged ? '💍 Engaged' : 'Mark Engaged'}
              </button>
              {/* Hide-from-directory toggle — removes the candidate from the public directory only */}
              <button
                type="button"
                onClick={async () => {
                  const nextVal = !candidate.hiddenFromDirectory;
                  const confirmMsg = nextVal
                    ? `Hide ${candidate.name || candidate.candidateCode} from the public directory?\nTheir profile will remain in the admin panel but will not appear in the portal directory, search, or detail views.`
                    : `Show ${candidate.name || candidate.candidateCode} in the public directory again?`;
                  if (!window.confirm(confirmMsg)) return;
                  try {
                    await axios.put(
                      `${API_URL}/api/ps/admin/candidates/${candidate.candidateCode}/hide`,
                      { hidden: nextVal },
                      { headers }
                    );
                    toast.success(nextVal ? 'Hidden from directory' : 'Visible in directory');
                    setSelectedUser((prev) => {
                      if (!prev) return prev;
                      const cands = [...(prev.candidates || [])];
                      cands[cidx] = { ...cands[cidx], hiddenFromDirectory: nextVal };
                      return { ...prev, candidates: cands };
                    });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to update visibility');
                  }
                }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${
                  candidate.hiddenFromDirectory
                    ? 'bg-gray-100 border-gray-400 text-gray-700 hover:bg-gray-200'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-500 hover:text-gray-800'
                }`}
                data-testid={`hide-directory-toggle-${cidx}`}
              >
                {candidate.hiddenFromDirectory
                  ? (<><Eye className="w-3 h-3" /> Show in Directory</>)
                  : (<><EyeOff className="w-3 h-3" /> Hide from Directory</>)}
              </button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h5 className="font-semibold text-xs text-gray-500 mb-2">PERSONAL</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {renderCandidateField(cidx, "Full Name", "name")}
                  {renderCandidateField(cidx, "Father's Name", "fatherName")}
                  {renderCandidateField(cidx, "Mother's Name", "motherName")}
                  {renderCandidateField(cidx, "Nukh", "nukh")}
                  {renderCandidateField(cidx, "Gender", "gender", 'text', isEditing ? ['Male / पुरुष', 'Female / महिला'] : null)}
                  {renderCandidateField(cidx, "Marital Status", "maritalStatus", 'text', isEditing ? ['Unmarried / अविवाहित', 'Divorced / तलाकशुदा', 'Widow / विधवा', 'Widower / विधुर'] : null)}
                  {renderCandidateField(cidx, "Date of Birth", "dateOfBirth", isEditing ? 'date' : 'text')}
                  {renderCandidateField(cidx, "Birth Time", "birthTime")}
                  {renderCandidateField(cidx, "Birth Place", "birthPlace")}
                </div>
              </div>
              <div>
                <h5 className="font-semibold text-xs text-gray-500 mb-2">PHYSICAL</h5>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {renderCandidateField(cidx, "Height", "height")}
                  {renderCandidateField(cidx, "Weight", "weight")}
                  {renderCandidateField(cidx, "Complexion", "complexion")}
                  {renderCandidateField(cidx, "Blood Group", "bloodGroup")}
                  {renderCandidateField(cidx, "Manglik", "mapiOrManglik")}
                </div>
              </div>
              <div>
                <h5 className="font-semibold text-xs text-gray-500 mb-2">FAMILY</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                  {renderCandidateField(cidx, "Unmarried Brothers", "unmarriedBrothers")}
                  {renderCandidateField(cidx, "Married Brothers", "marriedBrothers")}
                  {renderCandidateField(cidx, "Unmarried Sisters", "unmarriedSisters")}
                  {renderCandidateField(cidx, "Married Sisters", "marriedSisters")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {renderCandidateField(cidx, "Maternal Name", "maternalName")}
                  {renderCandidateField(cidx, "Nukh of Maternals", "nukhOfMaternals")}
                  {renderCandidateField(cidx, "Place of Maternals", "placeOfMaternals")}
                </div>
              </div>
              <div>
                <h5 className="font-semibold text-xs text-gray-500 mb-2">EDUCATION & CAREER</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {renderCandidateField(cidx, "Education", "education")}
                  {renderCandidateField(cidx, "Occupation", "occupation")}
                  {renderCandidateField(cidx, "Working At", "workingAt")}
                  {renderCandidateField(cidx, "Annual Income", "annualIncome")}
                </div>
              </div>
              <div>
                <h5 className="font-semibold text-xs text-gray-500 mb-2">CONTACT & ADDRESS</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-2">
                  {renderCandidateField(cidx, "Phone", "phone")}
                  {renderCandidateField(cidx, "WhatsApp", "whatsapp")}
                  {renderCandidateField(cidx, "Email", "email")}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {renderCandidateField(cidx, "Current Address", "currentAddress")}
                  {renderCandidateField(cidx, "Place", "currentPlace")}
                  {renderCandidateField(cidx, "State", "currentState")}
                  {renderCandidateField(cidx, "Country", "currentCountry")}
                  {renderCandidateField(cidx, "Native Place", "nativePlace")}
                  {renderCandidateField(cidx, "Native State", "nativeState")}
                </div>
              </div>
              <div>
                <h5 className="font-semibold text-xs text-gray-500 mb-2">ABOUT</h5>
                <div className="space-y-3">
                  {renderCandidateTextarea(cidx, "Hobbies", "hobbies")}
                  {renderCandidateTextarea(cidx, "About Candidate", "aboutCandidate")}
                  {renderCandidateTextarea(cidx, "Partner Preference", "partnerPreference")}
                </div>
              </div>
            </div>
          </Card>
        ))}

        {/* Attendees */}
        {(d.attendees?.length > 0 || isEditing) && (
          <Card className="p-4 mb-4">
            <div className="flex items-center justify-between mb-3 border-b pb-1">
              <h4 className="font-semibold text-sm text-gray-700">Attendees ({d.attendees?.length || 0})</h4>
              {isEditing && (
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-gray-500">Attending in Person</Label>
                  <button
                    type="button"
                    onClick={() => handleFamilyFieldChange('attendingInPerson', !d.attendingInPerson)}
                    className={`w-10 h-5 rounded-full transition-colors relative ${d.attendingInPerson ? 'bg-green-500' : 'bg-gray-300'}`}
                    data-testid="admin-attending-toggle"
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${d.attendingInPerson ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              )}
            </div>
            {d.attendees?.map((a, i) => (
              <div key={i} className={`py-3 ${i < d.attendees.length - 1 ? 'border-b' : ''}`}>
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Name</Label>
                        <Input value={a.name || ''} onChange={(e) => handleAttendeeFieldChange(i, 'name', e.target.value)} className="mt-1 h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Relation</Label>
                        <Input value={a.relation || ''} onChange={(e) => handleAttendeeFieldChange(i, 'relation', e.target.value)} className="mt-1 h-9 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Phone</Label>
                        <Input value={a.phone || ''} onChange={(e) => handleAttendeeFieldChange(i, 'phone', e.target.value)} className="mt-1 h-9 text-sm" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all" onClick={() => a.photoUrl && setPhotoModal({ url: resolveFileUrl(a.photoUrl), name: a.name || 'Attendee', rawUrl: a.photoUrl })}>
                        {a.photoUrl ? <img src={resolveFileUrl(a.photoUrl)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-6 h-6 text-gray-300" /></div>}
                      </div>
                      <Button size="sm" variant="outline" type="button" onClick={() => attendeePhotoRefs.current[i]?.click()} disabled={uploadingAttendeePhoto === i} data-testid={`admin-attendee-photo-upload-${i}`}>
                        {uploadingAttendeePhoto === i ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                        {a.photoUrl ? 'Change Photo' : 'Upload Photo'}
                      </Button>
                      <input ref={el => attendeePhotoRefs.current[i] = el} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleAdminAttendeePhotoUpload(d.id, i, e)} />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 border flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all" onClick={() => a.photoUrl && setPhotoModal({ url: resolveFileUrl(a.photoUrl), name: a.name || 'Attendee', rawUrl: a.photoUrl })} data-testid={`attendee-photo-thumb-${i}`}>
                      {a.photoUrl ? <img src={resolveFileUrl(a.photoUrl)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-gray-300" /></div>}
                    </div>
                    <div className="flex-1 flex justify-between text-sm">
                      <span className="font-medium">{a.name}</span>
                      <span className="text-gray-500">{a.relation} {a.phone && `| ${a.phone}`}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {isEditing && (
              <Button
                size="sm" variant="outline" className="mt-3"
                onClick={() => setEditData(prev => ({ ...prev, attendees: [...(prev.attendees || []), { name: '', relation: '', phone: '' }] }))}
                data-testid="admin-add-attendee-btn"
              >
                + Add Attendee
              </Button>
            )}
          </Card>
        )}

        {/* Payment */}
        <Card className="p-4 bg-green-50/50">
          <h4 className="font-semibold text-sm text-gray-700 mb-3 border-b pb-1">Payment Details</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {isEditing ? (
              <>
                <div>
                  <Label className="text-xs">Mode</Label>
                  <select value={d.paymentMode || ''} onChange={(e) => handleFamilyFieldChange('paymentMode', e.target.value)} className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                    <option value="">Select</option>
                    <option value="UPI">UPI</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Amount ({'\u20B9'})</Label>
                  <Input type="number" value={d.paymentAmount || ''} onChange={(e) => handleFamilyFieldChange('paymentAmount', parseInt(e.target.value) || 0)} className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Transaction ID</Label>
                  <Input value={d.paymentDetails || ''} onChange={(e) => handleFamilyFieldChange('paymentDetails', e.target.value)} className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Proof</Label>
                  {d.paymentProofUrl ? (<a href={resolveFileUrl(d.paymentProofUrl)} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline font-medium block mt-2">View Proof</a>) : <p className="text-sm text-gray-500 mt-2">{'\u2014'}</p>}
                </div>
              </>
            ) : (
              <>
                <div><p className="text-xs text-gray-400">Mode</p><p className="text-sm font-medium">{d.paymentMode || '\u2014'}</p></div>
                <div><p className="text-xs text-gray-400">Amount</p><p className="text-sm font-medium">{'\u20B9'}{d.paymentAmount || '\u2014'}</p></div>
                <div><p className="text-xs text-gray-400">Transaction ID</p><p className="text-sm font-medium">{d.paymentDetails || '\u2014'}</p></div>
                <div><p className="text-xs text-gray-400">Proof</p>{d.paymentProofUrl ? (<a href={resolveFileUrl(d.paymentProofUrl)} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline font-medium">View Proof</a>) : <p className="text-sm text-gray-500">{'\u2014'}</p>}</div>
              </>
            )}
          </div>
        </Card>
      </div>
      {/* Photo Lightbox Modal */}
      {photoModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPhotoModal(null)} data-testid="photo-lightbox-modal">
          <div className="relative max-w-2xl w-full bg-white rounded-xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <p className="font-semibold text-sm text-gray-800">{photoModal.name}</p>
              <div className="flex items-center gap-2">
                <a
                  href={photoModal.url}
                  download={`${photoModal.name.replace(/\s+/g, '_')}_photo.jpg`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-3 py-1.5 hover:bg-blue-50 transition-colors"
                  data-testid="photo-download-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <button onClick={() => setPhotoModal(null)} className="text-gray-400 hover:text-gray-700 p-1" data-testid="photo-lightbox-close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex items-center justify-center bg-gray-100 p-4" style={{ maxHeight: '70vh' }}>
              <img src={photoModal.url} alt={photoModal.name} className="max-w-full max-h-[65vh] object-contain rounded" />
            </div>
            {photoModal.rawUrl && (
              <div className="px-4 py-2 border-t bg-gray-50">
                <p className="text-xs text-gray-400 truncate" title={photoModal.rawUrl}>S3 Link: <a href={photoModal.rawUrl.startsWith('http') ? photoModal.rawUrl : resolveFileUrl(photoModal.rawUrl)} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">{photoModal.rawUrl}</a></p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Rejection Reason Modal - also needed in detail view */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="ps-reject-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-gray-900">Reject Registration</h2>
                <p className="text-sm text-gray-500">रजिस्ट्रेशन अस्वीकार करें</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="rejectionReasonDetail">Reason for Rejection <span className="text-red-500">*</span></Label>
                <Textarea
                  id="rejectionReasonDetail"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection (will be visible to the applicant)..."
                  rows={4}
                  className="mt-1"
                  data-testid="ps-reject-reason-input"
                />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setRejectModal(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmReject}
                  disabled={!rejectionReason.trim()}
                  data-testid="ps-reject-confirm-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
    );
  }

  // ============ MAIN LIST VIEW ============
  return (
    <div className="p-6" data-testid="admin-ps-list">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Heart className="w-6 h-6 text-orange-600" /> PS 2026 {attendingFilter === 'true' ? 'Attendees' : attendingFilter === 'false' ? 'Non-Attendees' : 'All Registrations'}
        </h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => window.location.href = '/admin/ps-new-registration'} className="bg-orange-600 hover:bg-orange-700" data-testid="admin-ps-new-reg-btn">
            <User className="w-4 h-4 mr-1" /> New Registration
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/ps-reports'} data-testid="admin-ps-reports-btn">
            <BarChart3 className="w-4 h-4 mr-1" /> Reports
          </Button>
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={exporting} data-testid="admin-ps-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          {attendingFilter === 'true' && (
            <>
              <Button variant="outline" size="sm" onClick={() => bulkUploadRef.current?.click()} disabled={bulkUploading}>
                {bulkUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {bulkUploading ? 'Uploading...' : 'Upload Fields'}
              </Button>
              <input ref={bulkUploadRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                onChange={(e) => { if (e.target.files[0]) handleBulkUpload(e.target.files[0]); }} />
              <Button variant="outline" size="sm" onClick={handleBulkRegenerate} disabled={regenerating} className="text-green-700 border-green-300 hover:bg-green-50">
                {regenerating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                {regenerating
                  ? (regenProgress.total > 0 ? `Sending ${regenProgress.current}/${regenProgress.total}…` : 'Starting…')
                  : 'Regenerate All ID Cards'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadA3} disabled={downloadingA3} className="text-purple-700 border-purple-300 hover:bg-purple-50" data-testid="admin-ps-a3-sheet-btn">
                {downloadingA3 ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                {downloadingA3
                  ? (a3Progress.total > 0 ? `Downloading ${a3Progress.current}/${a3Progress.total}…` : 'Generating…')
                  : 'A3 Print Sheet'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Families', value: stats.totalFamilies, color: 'bg-blue-50 text-blue-700' },
          { label: 'Candidates', value: stats.totalCandidates, color: 'bg-indigo-50 text-indigo-700' },
          { label: 'Pending', value: stats.pending, color: 'bg-yellow-50 text-yellow-700' },
          { label: 'Approved', value: stats.approved, color: 'bg-green-50 text-green-700' },
          { label: 'Rejected', value: stats.rejected, color: 'bg-red-50 text-red-700' },
          { label: 'Male / Female', value: `${stats.genderCounts?.['Male / \u092A\u0941\u0930\u0941\u0937'] || 0} / ${stats.genderCounts?.['Female / \u092E\u0939\u093F\u0932\u093E'] || 0}`, color: 'bg-purple-50 text-purple-700' },
        ].map(s => (
          <Card key={s.label} className={`p-4 ${s.color}`}>
            <p className="text-xs font-medium opacity-70">{s.label}</p>
            <p className="text-2xl font-bold">{s.value ?? 0}</p>
          </Card>
        ))}
      </div>

      {/* eBook Management */}
      <Card className="p-4 mb-6 border-l-4 border-l-green-500">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Post-Event eBook</h3>
              {ebook?.url ? (
                <p className="text-xs text-gray-500">{ebook.filename} {'\u2014'} {ebook.isPublished ? <span className="text-green-600 font-medium">Published</span> : <span className="text-yellow-600 font-medium">Unpublished</span>}</p>
              ) : <p className="text-xs text-gray-400">No eBook uploaded yet</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => ebookRef.current?.click()} disabled={uploadingEbook} data-testid="admin-ebook-upload-btn">
              {uploadingEbook ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}{ebook?.url ? 'Replace' : 'Upload'} PDF
            </Button>
            <input ref={ebookRef} type="file" accept="application/pdf" className="hidden" onChange={handleEbookUpload} />
            {ebook?.url && (
              <Button size="sm" onClick={toggleEbookPublish} className={ebook.isPublished ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700'} data-testid="admin-ebook-toggle-btn">
                {ebook.isPublished ? <><ToggleRight className="w-4 h-4 mr-1" /> Unpublish</> : <><ToggleLeft className="w-4 h-4 mr-1" /> Publish</>}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Search (Name, Email, Phone, City, Code)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search..." className="h-9" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} data-testid="admin-ps-search" />
              <Button size="sm" onClick={handleSearch} className="h-9"><Search className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="mt-1 h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-status-filter">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setShowFilters(!showFilters)} data-testid="admin-ps-filter-toggle">
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {activeFilterCount > 0 && <span className="ml-1 bg-orange-600 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>}
            {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
          {(searchTerm || activeFilterCount > 0) && (
            <Button variant="ghost" size="sm" className="h-9" onClick={clearAllFilters}>
              <X className="w-4 h-4 mr-1" /> Clear All
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Location Filters</p>
            <div className="flex flex-wrap gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Current Place / City</label>
                <input type="text" value={cityFilter} onChange={(e) => { setCityFilter(e.target.value); setPage(0); }} placeholder="e.g. Jodhpur" className="h-9 w-40 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-city" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Native Place</label>
                <input type="text" value={nativePlaceFilter} onChange={(e) => { setNativePlaceFilter(e.target.value); setPage(0); }} placeholder="e.g. Pali" className="h-9 w-40 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-native" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nukh</label>
                <select value={nukhFilter} onChange={(e) => { setNukhFilter(e.target.value); setPage(0); }} className="h-9 w-44 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-nukh">
                  <option value="">All Nukhs</option>
                  {filterOptions.nukhs.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Candidate Filters</p>
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                <select value={genderFilter} onChange={(e) => { setGenderFilter(e.target.value); setPage(0); }} className="h-9 w-44 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-gender">
                  <option value="">All</option>
                  <option value="Male">Male / पुरुष</option>
                  <option value="Female">Female / महिला</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Age Range</label>
                <select value={ageRangeFilter} onChange={(e) => { setAgeRangeFilter(e.target.value); setPage(0); }} className="h-9 w-36 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-age">
                  {PS_AGE_RANGES.map(r => <option key={r.label} value={r.label === 'All Ages' ? '' : r.label}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Education</label>
                <input type="text" value={educationFilter} onChange={(e) => { setEducationFilter(e.target.value); setPage(0); }} placeholder="e.g. MBA" className="h-9 w-40 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-education" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Occupation</label>
                <input type="text" value={occupationFilter} onChange={(e) => { setOccupationFilter(e.target.value); setPage(0); }} placeholder="e.g. Engineer" className="h-9 w-40 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-occupation" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Marital Status</label>
                <select value={maritalStatusFilter} onChange={(e) => { setMaritalStatusFilter(e.target.value); setPage(0); }} className="h-9 w-44 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-marital">
                  <option value="">All</option>
                  <option value="Unmarried">Unmarried / अविवाहित</option>
                  <option value="Divorced">Divorced / तलाकशुदा</option>
                  <option value="Widow">Widow / विधवा</option>
                  <option value="Widower">Widower / विधुर</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Engagement Status</label>
                <select value={engagementFilter} onChange={(e) => { setEngagementFilter(e.target.value); setPage(0); }} className="h-9 w-44 px-3 rounded-md border border-input bg-background text-sm" data-testid="admin-ps-filter-engagement">
                  <option value="">All</option>
                  <option value="false">Available / उपलब्ध</option>
                  <option value="true">💍 Engaged / सगाई हो चुकी</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-600" /></div>
      ) : users.length === 0 ? (
        <Card className="p-12 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="font-medium">No profiles found</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-ps-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Family</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Candidates</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Location</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Fee</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(user => {
                  const primary = user.candidates?.[0] || {};
                  const candCount = user.candidates?.length || 0;
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 border flex-shrink-0">
                            {primary.photoUrl ? <img src={resolveFileUrl(primary.photoUrl)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-gray-300" /></div>}
                          </div>
                          <div>
                            <p className="font-medium text-orange-700">{user.familyCode}</p>
                            <p className="text-xs text-gray-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{primary.name || '\u2014'}</p>
                        <p className="text-xs text-gray-400">{candCount > 1 ? `+${candCount - 1} more` : primary.gender || ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700">{primary.phone}</p>
                        <p className="text-xs text-gray-400">{primary.whatsapp && `WA: ${primary.whatsapp}`}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{primary.currentPlace}{primary.currentState ? `, ${primary.currentState}` : ''}</td>
                      <td className="px-4 py-3 text-gray-700 font-medium">{'\u20B9'}{user.paymentAmount?.toLocaleString()}</td>
                      <td className="px-4 py-3"><StatusBadge status={user.status} small /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openView(user)} data-testid={`admin-ps-view-${user.id}`}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => downloadIdCards(user.id, user.familyCode)} title="Download ID Cards"><Download className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => { setResetPasswordModal({ email: user.email, name: user.candidates?.[0]?.name || user.familyCode }); setNewPassword(''); }} title="Reset Password" data-testid={`admin-ps-reset-pwd-${user.id}`}><KeyRound className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-green-600" onClick={() => updateStatus(user.id, 'approved')}><Check className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleReject(user.id)}><X className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t bg-gray-50">
              <PaginationBar
                currentPage={page + 1}
                totalPages={totalPages}
                onPageChange={(p) => setPage(p - 1)}
                totalItems={total}
                pageSize={LIMIT}
                itemLabel="candidates"
                testIdPrefix="admin-ps"
              />
            </div>
          )}
        </Card>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetPasswordModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="admin-ps-reset-pwd-modal">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-gray-900">Reset Password</h2>
                <p className="text-sm text-gray-500">पासवर्ड रीसेट करें</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-1">Resetting password for:</p>
              <p className="font-semibold text-gray-900">{resetPasswordModal.name}</p>
              <p className="text-sm text-gray-500">{resetPasswordModal.email}</p>
            </div>
            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newPsPassword">New Password / नया पासवर्ड</Label>
                <Input id="newPsPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 characters)" required minLength={6} data-testid="admin-ps-reset-pwd-input" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setResetPasswordModal(null)}>Cancel</Button>
                <Button type="submit" disabled={resettingPassword} className="flex-1" data-testid="admin-ps-reset-pwd-submit">
                  {resettingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Reset Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Rejection Reason Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="ps-reject-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-gray-900">Reject Registration</h2>
                <p className="text-sm text-gray-500">रजिस्ट्रेशन अस्वीकार करें</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="rejectionReason">Reason for Rejection <span className="text-red-500">*</span></Label>
                <Textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection (will be visible to the applicant)..."
                  rows={4}
                  className="mt-1"
                  data-testid="ps-reject-reason-input"
                />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setRejectModal(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmReject}
                  disabled={!rejectionReason.trim()}
                  data-testid="ps-reject-confirm-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PSManagement;
